/**
 * An accordion row: a collapsed line with a title and a one-line summary that
 * opens to its details. It is a native `<details>` element, so the keyboard,
 * the screen reader, and the "open" state all work with no script. The chevron
 * on the right turns by CSS when the row opens.
 */
import { createChevron } from './chevron'
import { createElement, createTextElement } from './dom'

/** What an accordion row shows. */
export interface AccordionOptions {
  /** The row's class names, after `accordion`. */
  readonly className?: string
  readonly title: string
  /** One line under the title. */
  readonly summary: string
  /** The details the row opens to. */
  readonly panel: readonly Node[]
}

/** Builds one closed accordion row. */
export const createAccordion = (options: AccordionOptions): HTMLDetailsElement => {
  const row = createElement('details', ['accordion', options.className].filter(Boolean).join(' '))

  const header = createElement('summary', 'accordion-header')
  const text = createElement('span', 'accordion-text')
  text.append(
    createTextElement('span', options.title, 'accordion-title'),
    createTextElement('span', options.summary, 'accordion-summary'),
  )
  header.append(text, createChevron('disclosure'))

  const panel = createElement('div', 'accordion-panel')
  panel.append(...options.panel)

  row.append(header, panel)
  return row
}
